import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { StatCard } from "@/components/dashboard/StatCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Users, UserPlus, BookOpen, ClipboardCheck, DollarSign,
  HeadphonesIcon, Shield, Activity, TrendingUp, AlertTriangle, GraduationCap,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import {
  ChartContainer, ChartTooltip, ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { PrincipalDashboard } from "@/components/dashboard/PrincipalDashboard";
import { DeputyPrincipalDashboard } from "@/components/dashboard/DeputyPrincipalDashboard";
import { StudentDashboard } from "@/components/dashboard/StudentDashboard";
import { HodDashboard } from "@/components/dashboard/HodDashboard";
import { AnnouncementsCard } from "@/components/dashboard/AnnouncementsCard";
import { PreRegDetailDialog } from "@/components/dashboard/PreRegDetailDialog";
import { ApplicationsDetailDialog } from "@/components/dashboard/ApplicationsDetailDialog";
import { FeesDetailDialog } from "@/components/dashboard/FeesDetailDialog";
import { ApprovalsDetailDialog } from "@/components/dashboard/ApprovalsDetailDialog";
import { ServiceRequestsDetailDialog } from "@/components/dashboard/ServiceRequestsDetailDialog";
import { DisciplineCasesDetailDialog } from "@/components/dashboard/DisciplineCasesDetailDialog";

const roleLabels: Record<string, string> = {
  superadmin: "Super Admin", principal: "Principal", deputy_principal: "Deputy Principal",
  head_admission: "Head of Admission", head_administration: "Head of Administration",
  head_academic: "Head of Academic", head_examination: "Head of Examination",
  head_accounting: "Head of Accounting", head_student_service: "Head of Student Services",
  head_discipline: "Head of Discipline", hod_cmt: "HoD CMT", hod_pst: "HoD PST",
  hod_sw: "HoD SW", it_incharge: "IT Incharge", security_man: "Security Man",
  cr_cmt4: "CR CMT4", cr_cmt5: "CR CMT5", cr_cmt6: "CR CMT6",
  cr_pst4: "CR PST4", cr_pst5: "CR PST5", cr_pst6: "CR PST6",
  cr_sw4: "CR SW4", cr_sw5: "CR SW5", cr_sw6: "CR SW6", auditor: "Auditor", student: "Student",
};

const CHART_COLORS = ["hsl(var(--primary))", "hsl(var(--accent-orange))", "hsl(var(--accent-blue))", "hsl(var(--destructive))"];

const Dashboard = () => {
  const { user, roles, hasRole } = useAuth();
  const navigate = useNavigate();
  const primaryRole = roles[0];
  const isSuperadmin = hasRole("superadmin");
  const [preRegDialogOpen, setPreRegDialogOpen] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [feesDialogOpen, setFeesDialogOpen] = useState(false);
  const [approvalsDialogOpen, setApprovalsDialogOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [disciplineDialogOpen, setDisciplineDialogOpen] = useState(false);
  const isPrincipal = hasRole("principal");
  const isDeputy = hasRole("deputy_principal");
  const isStudent = hasRole("student") && !isSuperadmin && !isPrincipal && !isDeputy;
  const isHod = (hasRole("hod_cmt") || hasRole("hod_pst") || hasRole("hod_sw") || hasRole("it_incharge")) && !isSuperadmin;
  const needsGeneralStats = isSuperadmin || (!isPrincipal && !isDeputy && !isStudent && !isHod);

  const { data: studentCount = 0 } = useQuery({
    queryKey: ["dashboard-students-count"],
    queryFn: async () => { const { count } = await supabase.from("students").select("*", { count: "exact", head: true }); return count ?? 0; },
    enabled: needsGeneralStats,
  });

  const { data: appCounts } = useQuery({
    queryKey: ["dashboard-applications-count"],
    queryFn: async () => {
      const { data } = await supabase.from("applications").select("status");
      return { total: data?.length ?? 0, pending: data?.filter(a => a.status === "pending").length ?? 0, approved: data?.filter(a => a.status === "approved").length ?? 0, rejected: data?.filter(a => a.status === "rejected").length ?? 0 };
    },
    enabled: needsGeneralStats,
  });

  const { data: courseCount = 0 } = useQuery({
    queryKey: ["dashboard-courses-count"],
    queryFn: async () => { const { count } = await supabase.from("courses").select("*", { count: "exact", head: true }); return count ?? 0; },
    enabled: needsGeneralStats,
  });

  const { data: invoiceStats } = useQuery({
    queryKey: ["dashboard-invoice-stats"],
    queryFn: async () => {
      const { data } = await supabase.from("invoices").select("amount, status, due_date");
      const total = data?.reduce((s, i) => s + Number(i.amount), 0) ?? 0;
      const unpaid = data?.filter(i => i.status === "unpaid") ?? [];
      return { total, outstanding: unpaid.reduce((s, i) => s + Number(i.amount), 0), overdue: unpaid.filter(i => i.due_date && new Date(i.due_date) < new Date()).length, unpaidCount: unpaid.length };
    },
    enabled: needsGeneralStats,
  });

  const { data: paymentTotal = 0 } = useQuery({
    queryKey: ["dashboard-payments-total"],
    queryFn: async () => { const { data } = await supabase.from("payments").select("amount"); return data?.reduce((s, p) => s + Number(p.amount), 0) ?? 0; },
    enabled: needsGeneralStats,
  });

  const { data: serviceStats } = useQuery({
    queryKey: ["dashboard-service-stats"],
    queryFn: async () => {
      const { data } = await supabase.from("service_requests").select("status");
      return { total: data?.length ?? 0, open: data?.filter(r => r.status === "open").length ?? 0, escalated: data?.filter(r => r.status === "escalated").length ?? 0, resolved: data?.filter(r => r.status === "resolved").length ?? 0 };
    },
    enabled: needsGeneralStats,
  });

  const { data: disciplineStats } = useQuery({
    queryKey: ["dashboard-discipline-stats"],
    queryFn: async () => {
      const { data } = await supabase.from("discipline_cases").select("status");
      return { total: data?.length ?? 0, open: data?.filter(c => c.status === "open" || c.status === "investigation").length ?? 0, hearing: data?.filter(c => c.status === "hearing").length ?? 0, resolved: data?.filter(c => c.status === "resolved").length ?? 0 };
    },
    enabled: needsGeneralStats,
  });

  const { data: examStats } = useQuery({
    queryKey: ["dashboard-exam-stats"],
    queryFn: async () => {
      const { data } = await supabase.from("exam_results").select("status, marks");
      return { total: data?.length ?? 0, pending: data?.filter(r => r.status === "pending").length ?? 0, published: data?.filter(r => r.status === "published").length ?? 0, avgMarks: data?.length ? Math.round(data.reduce((s, r) => s + (Number(r.marks) || 0), 0) / data.length) : 0 };
    },
    enabled: needsGeneralStats,
  });

  const { data: pendingApprovals = 0 } = useQuery({
    queryKey: ["dashboard-pending-approvals"],
    queryFn: async () => {
      const { count } = await supabase.from("approvals").select("*", { count: "exact", head: true }).eq("status", "pending");
      return count ?? 0;
    },
    enabled: isSuperadmin,
  });

  const { data: preRegStats } = useQuery({
    queryKey: ["dashboard-prereg-stats"],
    queryFn: async () => {
      const { data } = await supabase.from("student_pre_registrations").select("onboarding_status, otp_code");
      const total = data?.length ?? 0;
      const otpPending = data?.filter(r => r.onboarding_status === "pre_registered" && r.otp_code).length ?? 0;
      const awaitingSignup = data?.filter(r => r.onboarding_status === "pre_registered" && !r.otp_code).length ?? 0;
      const active = data?.filter(r => r.onboarding_status === "active").length ?? 0;
      return { total, otpPending, awaitingSignup, active };
    },
    enabled: isSuperadmin,
  });

  const { data: recentActivity = [] } = useQuery({
    queryKey: ["dashboard-recent-activity"],
    queryFn: async () => {
      const { data } = await supabase.from("audit_logs").select("*").order("created_at", { ascending: false }).limit(5);
      return (data ?? []).map((l: any) => ({ action: `${l.action} on ${l.table_name.replace("_", " ")}`, time: l.created_at }));
    },
    enabled: isSuperadmin,
  });

  const applicationChartData = [
    { name: "Pending", value: appCounts?.pending ?? 0 },
    { name: "Approved", value: appCounts?.approved ?? 0 },
    { name: "Rejected", value: appCounts?.rejected ?? 0 },
  ];

  const overviewBarData = [
    { name: "Students", value: studentCount },
    { name: "Courses", value: courseCount },
    { name: "Applications", value: appCounts?.total ?? 0 },
    { name: "Results", value: examStats?.total ?? 0 },
  ];

  const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`;
  const timeAgo = (t: string) => {
    const diff = Date.now() - new Date(t).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div>
          {!isStudent && <h1 className="text-lg sm:text-xl md:text-2xl font-bold truncate">Welcome, {user?.email?.split("@")[0]}</h1>}
          {!isStudent && <p className="text-muted-foreground text-xs sm:text-sm">{roleLabels[primaryRole] || "User"} Dashboard</p>}
        </div>

        {isPrincipal && !isSuperadmin && <PrincipalDashboard />}
        {isDeputy && !isSuperadmin && !isPrincipal && <DeputyPrincipalDashboard />}
        {isStudent && <StudentDashboard />}
        {isHod && <HodDashboard />}
        {hasRole("security_man") && !isSuperadmin && (
          <div className="grid gap-2 sm:gap-3 grid-cols-1">
            <StatCard
              title="Attendance Module"
              value="Scan & Track"
              icon={ClipboardCheck}
              description="Open the attendance scanner and session tools"
              onClick={() => navigate("/attendance")}
            />
          </div>
        )}

        {isSuperadmin && (
          <>
            <div className="grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-2 lg:grid-cols-5">
              <StatCard title="Total Students" value={studentCount.toLocaleString()} icon={Users} description={`${courseCount} active courses`} trend="up" />
              <StatCard title="New Applications" value={appCounts?.total ?? 0} icon={UserPlus} description={`${appCounts?.pending ?? 0} pending review`} onClick={() => setAppDialogOpen(true)} />
              <StatCard title="Revenue Collected" value={fmt(paymentTotal)} icon={DollarSign} description={`${fmt(invoiceStats?.outstanding ?? 0)} outstanding`} trend="up" onClick={() => setFeesDialogOpen(true)} />
              <StatCard title="Pending Approvals" value={pendingApprovals} icon={ClipboardCheck} description="Awaiting review" onClick={() => setApprovalsDialogOpen(true)} />
              <StatCard title="Pre-Registrations" value={preRegStats?.total ?? 0} icon={GraduationCap} description={`${preRegStats?.otpPending ?? 0} OTP pending · ${preRegStats?.awaitingSignup ?? 0} awaiting signup`} onClick={() => setPreRegDialogOpen(true)} />
            </div>

            <div className="grid gap-2 sm:gap-3 grid-cols-1 lg:grid-cols-2">
              <Card>
                <CardHeader><CardTitle className="text-lg">Institution Overview</CardTitle></CardHeader>
                <CardContent>
                  <ChartContainer config={{ students: { label: "Students", color: "hsl(var(--primary))" }, courses: { label: "Courses", color: "hsl(var(--accent-orange))" }, applications: { label: "Applications", color: "hsl(var(--accent-blue))" }, results: { label: "Results", color: "hsl(var(--destructive))" } }} className="h-[250px]">
                    <BarChart data={overviewBarData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg">Application Status</CardTitle></CardHeader>
                <CardContent>
                  <ChartContainer config={{ pending: { label: "Pending", color: "hsl(var(--accent-orange))" }, approved: { label: "Approved", color: "hsl(var(--primary))" }, rejected: { label: "Rejected", color: "hsl(var(--destructive))" } }} className="h-[250px]">
                    <PieChart>
                      <Pie data={applicationChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" nameKey="name" label>
                        {applicationChartData.map((_, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                      </Pie>
                      <ChartTooltip content={<ChartTooltipContent />} />
                    </PieChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {hasRole("head_admission") && !isSuperadmin && (
          <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Pending Applications" value={appCounts?.pending ?? 0} icon={UserPlus} description="Needs review" onClick={() => setAppDialogOpen(true)} />
            <StatCard title="Approved" value={appCounts?.approved ?? 0} icon={Users} trend="up" onClick={() => setAppDialogOpen(true)} />
            <StatCard title="Rejected" value={appCounts?.rejected ?? 0} icon={AlertTriangle} onClick={() => setAppDialogOpen(true)} />
            <StatCard title="Total Applications" value={appCounts?.total ?? 0} icon={TrendingUp} onClick={() => setAppDialogOpen(true)} />
          </div>
        )}

        {hasRole("head_accounting") && !isSuperadmin && (
          <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Outstanding Fees" value={fmt(invoiceStats?.outstanding ?? 0)} icon={DollarSign} description={`${invoiceStats?.unpaidCount ?? 0} students`} onClick={() => setFeesDialogOpen(true)} />
            <StatCard title="Collected" value={fmt(paymentTotal)} icon={TrendingUp} trend="up" />
            <StatCard title="Overdue Invoices" value={invoiceStats?.overdue ?? 0} icon={AlertTriangle} trend="down" onClick={() => setFeesDialogOpen(true)} />
            <StatCard title="Total Invoiced" value={fmt(invoiceStats?.total ?? 0)} icon={Activity} />
          </div>
        )}

        {hasRole("head_examination") && !isSuperadmin && (
          <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Pending Results" value={examStats?.pending ?? 0} icon={ClipboardCheck} description="Awaiting approval" />
            <StatCard title="Published" value={examStats?.published ?? 0} icon={TrendingUp} />
            <StatCard title="Total Results" value={examStats?.total ?? 0} icon={Activity} />
            <StatCard title="Average Marks" value={`${examStats?.avgMarks ?? 0}%`} icon={Users} trend="up" />
          </div>
        )}

        {hasRole("head_student_service") && !isSuperadmin && (
          <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Open Requests" value={serviceStats?.open ?? 0} icon={HeadphonesIcon} onClick={() => setServiceDialogOpen(true)} />
            <StatCard title="Resolved" value={serviceStats?.resolved ?? 0} icon={TrendingUp} trend="up" onClick={() => setServiceDialogOpen(true)} />
            <StatCard title="Escalated" value={serviceStats?.escalated ?? 0} icon={AlertTriangle} trend="down" onClick={() => setServiceDialogOpen(true)} />
            <StatCard title="Total" value={serviceStats?.total ?? 0} icon={Activity} onClick={() => setServiceDialogOpen(true)} />
          </div>
        )}

        {hasRole("head_discipline") && !isSuperadmin && (
          <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Open Cases" value={disciplineStats?.open ?? 0} icon={Shield} onClick={() => setDisciplineDialogOpen(true)} />
            <StatCard title="Hearings" value={disciplineStats?.hearing ?? 0} icon={ClipboardCheck} onClick={() => setDisciplineDialogOpen(true)} />
            <StatCard title="Resolved" value={disciplineStats?.resolved ?? 0} icon={TrendingUp} onClick={() => setDisciplineDialogOpen(true)} />
            <StatCard title="Total" value={disciplineStats?.total ?? 0} icon={Activity} onClick={() => setDisciplineDialogOpen(true)} />
          </div>
        )}

        {!isStudent && <AnnouncementsCard />}

        {roles.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Welcome to St. David College</CardTitle>
              <CardDescription>Your account has been created. Please contact the administrator to assign your role.</CardDescription>
            </CardHeader>
          </Card>
        )}

        {isSuperadmin && (
          <div className="grid gap-2 sm:gap-3 grid-cols-1 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <CardDescription>Latest changes from audit log</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentActivity.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>}
                  {recentActivity.map((item: any, i: number) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="h-2 w-2 rounded-full bg-primary" />
                        <span className="text-sm capitalize">{item.action}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{timeAgo(item.time)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
                <CardDescription>Frequently used actions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: "Pre-Register Students", icon: GraduationCap, path: "/student-pre-registration" },
                    { label: "New Application", icon: UserPlus, path: "/admission" },
                    { label: "Register Course", icon: BookOpen, path: "/academic" },
                    { label: "Record Payment", icon: DollarSign, path: "/accounting" },
                    { label: "Enter Marks", icon: ClipboardCheck, path: "/examination" },
                    { label: "New Request", icon: HeadphonesIcon, path: "/student-services" },
                    { label: "Report Incident", icon: Shield, path: "/discipline" },
                    { label: "Manage Users", icon: Users, path: "/user-management" },
                  ].map((item, i) => (
                    <button key={i} onClick={() => navigate(item.path)} className="flex items-center gap-2 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-sm">
                      <item.icon className="h-4 w-4 text-primary" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <PreRegDetailDialog open={preRegDialogOpen} onOpenChange={setPreRegDialogOpen} />
      <ApplicationsDetailDialog open={appDialogOpen} onOpenChange={setAppDialogOpen} />
      <FeesDetailDialog open={feesDialogOpen} onOpenChange={setFeesDialogOpen} />
      <ApprovalsDetailDialog open={approvalsDialogOpen} onOpenChange={setApprovalsDialogOpen} />
      <ServiceRequestsDetailDialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen} />
      <DisciplineCasesDetailDialog open={disciplineDialogOpen} onOpenChange={setDisciplineDialogOpen} />
    </AppLayout>
  );
};

export default Dashboard;
